import { useCallback } from "react";

import { ConsentManagementConfigVersion } from "@/api";
import {
  ConsentDraftState,
  ConsentTargeting,
  currentTargeting,
  draftFingerprint,
} from "./targeting";
import { ConsentManagerData } from "./types";

export const DRAFT_TARGETS_CHANGED =
  "Your banner’s rules changed on the Consent Manager page. Review them here and save again.";

/** The targeting to show and save. Only user edits are stored. */
export function useCurrentTargeting(
  data: ConsentManagerData,
  draftState: ConsentDraftState,
): {
  targeting: ConsentTargeting;
  /** Stores edited targeting against the draft on screen. */
  storeTargeting: (targeting: ConsentTargeting) => Partial<ConsentManagerData>;
} {
  const fingerprint = draftFingerprint(draftState.draft);
  const storeTargeting = useCallback(
    (next: ConsentTargeting) => ({
      targeting: next,
      targetingDraft: fingerprint,
    }),
    [fingerprint],
  );
  return { targeting: currentTargeting(data, draftState), storeTargeting };
}

/**
 * What a save writes: the stored edits, if they're the targeting on screen and
 * the server's draft hasn't changed since. Otherwise the screen shows the
 * draft's own targets and there's nothing to write.
 */
export function targetingToSave(
  data: Pick<ConsentManagerData, "targeting" | "targetingDraft">,
  shownDraft: ConsentManagementConfigVersion | undefined,
  latest: ConsentManagementConfigVersion,
): ConsentTargeting | null {
  const madeOn = data.targetingDraft ?? null;
  if (!data.targeting || madeOn !== draftFingerprint(shownDraft)) {
    return null;
  }
  if (madeOn !== draftFingerprint(latest)) {
    throw new Error(DRAFT_TARGETS_CHANGED);
  }
  return data.targeting;
}
